/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/

/**
 * Helper functions for handling blob URLs with Private Endpoints
 * 
 * When Azure Blob Storage has Private Endpoints enabled and public access disabled,
 * browsers cannot access blobs directly even with SAS tokens. This helper provides
 * methods to use the backend proxy when needed.
 */

import { CoreAPI_URLs } from 'constants/http.const';
import { AssetInterface, HeaderEnum } from 'medicalsystem-shared';

/**
 * Configuration for blob proxy behavior
 */
const BLOB_PROXY_CONFIG = {
  // Enable proxy for all blobs when Private Endpoints are enabled
  // Note: In Create React App, environment variables must be prefixed with REACT_APP_
  USE_PROXY_FOR_PRIVATE_ENDPOINTS: process.env.REACT_APP_USE_BLOB_PROXY_FOR_PRIVATE_ENDPOINTS === 'true',
  
  // Base path for proxy endpoints
  PROXY_BASE_PATH: CoreAPI_URLs.PROXY_BASE_PATH,
  
  // Categories that should always use proxy (sensitive data)
  ALWAYS_PROXY_CATEGORIES: ['medical-records', 'confidential'],
  
  // File types that should use proxy
  PROXY_MIMETYPES: {
    images: [
      'image/jpeg', 
      'image/png', 
      'image/gif', 
      'image/webp', 
      'image/svg+xml',
      'image/x-icon',
      'image/bmp',
      'image/tiff'
    ],
    documents: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ],
    video: [
      'video/mp4',
      'video/mpeg',
      'video/webm',
      'video/ogg',
      'video/quicktime',
      'video/x-msvideo'
    ],
    audio: [
      'audio/mpeg',
      'audio/wav',
      'audio/ogg',
      'audio/mp4',
      'audio/webm',
      'audio/x-m4a'
    ],
    text: [
      'text/plain',
      'text/csv',
      'text/html',
      'text/css',
      'text/javascript',
      'application/json',
      'application/xml',
      'text/xml'
    ]
  }
};

/**
 * Interface for blob proxy configuration response containing URL and optional headers.
 * Used to configure authenticated asset requests through Azure blob proxy.
 */
export interface BlobProxyConfigInteface {
  url: string;
  headers?: Record<string, string>
}

/**
 * Gets the appropriate URL and headers for an asset (proxy or direct)
 * @param asset - Asset from the shared package
 * @returns Object with url and optional headers for axios
 */
export const getAssetUrl = (asset: AssetInterface): BlobProxyConfigInteface => {
  if (!shouldUseProxy()) {
    // Use original URL (with SAS token)
    return { url: asset.url };
  }
  
  // Extract asset ID from URL or use provided ID
  const assetId = asset.id;
  
  if (!assetId) {
    console.warn('Could not extract asset ID, falling back to original URL');
    return { url: asset.url };
  }
  
  // Determine endpoint type based on mimetype
  // Note: Backend currently only supports images, documents, and file endpoints
  let endpoint = 'file'; // Default endpoint
  
  if (asset.mimetype) {
    const mimetypeStr = typeof asset.mimetype === 'string' ? asset.mimetype : String(asset.mimetype);
    if (BLOB_PROXY_CONFIG.PROXY_MIMETYPES.images.includes(mimetypeStr)) {
      endpoint = 'images';
    } else if (BLOB_PROXY_CONFIG.PROXY_MIMETYPES.documents.includes(mimetypeStr)) {
      endpoint = 'documents';
    }
    // Other categories (video, audio, text) will use the generic 'file' endpoint
  }
  
  // Build proxy URL and headers
  // Headers are used because axios (in useAuthenticatedAsset hook) CAN send them
  const url = `${BLOB_PROXY_CONFIG.PROXY_BASE_PATH}/${endpoint}`;
  const headers = {
    [HeaderEnum.RESOURCE_ID]: assetId
  };
  
  return { url, headers };
};

/**
 * Determines if an asset should use the proxy endpoint
 * @returns true if proxy should be used
 */
export const shouldUseProxy = (): boolean => {
     // Use proxy for all assets when Private Endpoints are enabled
  // This can be made configurable based on environment variables
  if (BLOB_PROXY_CONFIG.USE_PROXY_FOR_PRIVATE_ENDPOINTS) {
    return true;
  }
  
  return false;
};

/**
 * Checks if a file is a Microsoft Office document based on MIME type.
 * Supports Word (.doc, .docx), Excel (.xls, .xlsx), and PowerPoint (.ppt, .pptx).
 * @param mimeType - MIME type string to check
 * @returns True if the file is an Office document
 */
export const isOfficeDocument = (mimeType: string) => {
    return mimeType.includes('application/msword') || 
           mimeType.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document') ||
           mimeType.includes('application/vnd.ms-excel') ||
           mimeType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') ||
           mimeType.includes('application/vnd.ms-powerpoint') ||
           mimeType.includes('application/vnd.openxmlformats-officedocument.presentationml.presentation');
  };

/**
 * Checks if a document can be displayed in a basic text/document viewer.
 * Supports plain text, CSV, HTML, XML, JSON, and RTF formats.
 * @param mimeType - MIME type string to check
 * @returns True if the file can be displayed in a document viewer
 */
export const canUseDocViewer = (mimeType: string) => {
    const docViewerTypes = [
      'text/plain',
      'text/csv',
      'text/html',
      'text/xml',
      'application/json',
      'application/rtf'
    ];
    return docViewerTypes.some(type => mimeType.includes(type));
  };

/**
 * Categorizes a file into a specific type based on its MIME type.
 * Used for determining appropriate viewer/renderer components.
 * @param mimeType - MIME type string to categorize
 * @returns File category: 'pdf', 'image', 'video', 'audio', 'office', 'text', or 'other'
 */
export const getFileCategory = (mimeType: string): 'pdf' | 'image' | 'video' | 'audio' | 'office' | 'text' | 'other' => {
  if (mimeType.includes('pdf')) return 'pdf';
  if (mimeType.includes('image/')) return 'image';
  if (mimeType.includes('video/')) return 'video';
  if (mimeType.includes('audio/')) return 'audio';
  if (mimeType.includes('doc') || mimeType.includes('docx') || 
      mimeType.includes('xls') || mimeType.includes('xlsx') || 
      mimeType.includes('ppt') || mimeType.includes('pptx')) return 'office';
  if (mimeType.includes('text/')) return 'text';
  return 'other';
};

/**
 * Determines if a file can be displayed inline in the browser without download.
 * Checks against supported viewable formats including PDFs, images, videos, audio, text, and Office documents.
 * @param mimeType - MIME type string to check
 * @returns True if the file can be viewed inline in the browser
 */
export const isViewableInline = (mimeType: string) => {
    const viewableTypes = [
      'application/pdf',
      'image/',
      'video/',
      'audio/',
      'text/',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ];
    return viewableTypes.some(type => mimeType.includes(type));
  };

/**
 * Configuration helper to enable/disable proxy based on environment
 * @param useProxy - Whether to use proxy for all assets
 */
export const configureProxyUsage = (useProxy: boolean): void => {
  BLOB_PROXY_CONFIG.USE_PROXY_FOR_PRIVATE_ENDPOINTS = useProxy;
};

// Export configuration for external access
export { BLOB_PROXY_CONFIG as PROXY_CONFIG };
